// apps/provider/src/utils/pnDue.js
// ONE "progress note due" predicate shared by the calendar (appointment
// shading, useCalendarAlerts) and CreateVisitModal (forced note type). Both
// surfaces must agree: an appointment is shaded exactly when Start Visit for
// that case is forced.
//
// PN-due fires ONLY for (a) a visit_milestone violation whose required_action
// is a progress-note-family action, or (b) an actually expired plan of care:
// either the case's current POC has certification_end in the past (store /
// isPocExpired flag, no rule needed) or a documentation_trigger rule with
// trigger poc_expiration fired for the EXPIRED case (not its days_before
// pre-expiry warning). Start-date POC rules (poc_certification) and
// treatment-gap rules produce text alerts only.
//
// The whole thing is a documentation/recert workflow for orgs with a real
// non-cash billing mode; callers coerce a missing setting to 'cash'.
//
// Org setting settings.documentation.poc_alerts_medicare_only: the POC
// certification side (isPocExpired flag, poc_expiration rule violations, POC
// expiry text) applies only to cases whose billing coverage is Medicare or
// Medicare Advantage. Callers gate the INPUTS to derivePnDue with
// isMedicareCoverage / isPocExpirationViolation so both surfaces still see
// identical inputs; visit milestones are unaffected.

import { resolveCaseBillingCoverage } from '@prism-ehr/shared/utils/caseBillingCoverage';
import { normalizePlanType } from '@prism-ehr/shared/constants/insurance';

export const PN_ACTIONS = new Set(['progress_note', 're_evaluation', 'poc_renewal', 'new_evaluation']);

/**
 * Case billing coverage (case billing_coverage override, else the patient's
 * primary) is Medicare or Medicare Advantage. Same resolution as the Medicare
 * discharge logic in CreateVisitModal.
 */
export function isMedicareCoverage(caseRow, patient) {
  const planType = normalizePlanType(resolveCaseBillingCoverage(caseRow, patient).insurance?.plan_type);
  return planType === 'medicare' || planType === 'medicare_advantage';
}

/** A documentation_trigger violation from the poc_expiration trigger (either firing). */
export function isPocExpirationViolation(v) {
  return !!v && v.category === 'documentation_trigger' && v.trigger === 'poc_expiration';
}

/** @param {import('@prism-ehr/shared/types/api/billingRules').BillingRuleViolation} v */
export function isPnDueViolation(v) {
  if (!v) return false;
  if (v.category === 'visit_milestone') return PN_ACTIONS.has(v.required_action);
  if (v.category === 'documentation_trigger') {
    return v.trigger === 'poc_expiration' && v.expired === true;
  }
  return false;
}

/**
 * @param {{ violations?: Array, pocExpired?: boolean, billingMode?: string }} args
 *  violations: evaluated visit_milestone / documentation_trigger violations for the case
 *  pocExpired: the case's current POC has already expired (isPocExpired)
 *  billingMode: org settings.billing_mode; absent or 'cash' disables PN-due entirely
 * @returns {{ pnDue: boolean, hardBlock: boolean }}
 */
export function derivePnDue({ violations = [], pocExpired = false, billingMode } = {}) {
  const enabled = !!billingMode && billingMode !== 'cash';
  if (!enabled) return { pnDue: false, hardBlock: false };
  const pnViolations = (violations || []).filter(isPnDueViolation);
  return {
    pnDue: !!pocExpired || pnViolations.length > 0,
    hardBlock: pnViolations.some(v => v.enforcement_level === 'hard_block'),
  };
}
